import { useCallback, useEffect, useRef, useState } from "react";
import {
  defaultTransportForCapability,
  loadPreferredPasskeyTransportOrNull,
  savePreferredPasskeyTransport,
  type CapabilitySnapshot,
  type PasskeyTransport,
} from "@pureid/auth-core";

/**
 * useCeremonyTransport — shared transport-state owner for every
 * passkey ceremony surface in PureID.
 *
 * # Why this exists
 *
 * Sign-in (LoginSurface), enrollment (LoginSurface again, via the
 * `enroll-pick-hint` step), the standalone "add a passkey" dialog,
 * and step-up re-verification (StepUpSurface) all share the same
 * "remember the user's prior transport pick across surfaces"
 * affordance:
 *
 *   1. Initial picker selection comes from the four-layer
 *      cascade (see "Initial value resolution" below).
 *   2. The picker is fully controlled by the surface; the user's
 *      click updates the local state.
 *   3. On a SUCCESSFUL ceremony, the surface persists the value via
 *      `savePreferredPasskeyTransport()` so the next surface's
 *      picker lands on the same card.
 *
 * Every surface used to inline these three responsibilities. The
 * step-up surface in particular drifted — it loaded the preference
 * but never saved on success, so a user who picked QR at sign-in,
 * verified via QR at step-up, and returned later got the original
 * sign-in pick (correct) — but a user who switched transports at
 * step-up never had their new pick remembered.
 *
 * One hook, three behaviours, four call sites. A future pick rule
 * (capability-aware default, A/B test, server-config-driven default)
 * lands in exactly one place.
 *
 * # Initial value resolution
 *
 * Four layers, highest-priority first:
 *
 *   1. The user's last-saved preference (localStorage). Stored on
 *      every successful ceremony; survives across surfaces and
 *      sessions on the SAME device. If present, it always wins —
 *      a user who consciously picked phone last time keeps phone.
 *      The device-local cache is the fastest signal (instant read,
 *      no network).
 *   2. The user's SERVER-side cross-device preference (H-1 /
 *      §4.8). Surfaced via /identify's KnownUserResult.
 *      preferred_transport, this closes the "fresh device loses
 *      my personal pick" gap: a returning user on a colleague's
 *      laptop sees their cross-device pick instead of falling
 *      straight to the tenant/device recommender. Vocabulary on
 *      the wire is `this_device | phone_qr | security_key`; the
 *      hook translates `phone_qr → other_device` to align with
 *      the PasskeyTransport enum.
 *   3. The server-side RECOMMENDER's primary method, translated
 *      through `methodToTransport()`. This is the value an
 *      Executive playbook caller passes so the picker lands on
 *      `security_key` instead of the legacy mobile-first
 *      fallback. Only consulted when neither localStorage nor the
 *      server-side pref is set. Pass `null` (or omit) if no
 *      recommendation is available; the hook falls through to
 *      layer 4.
 *   4. The capability-aware product default — mobile-first
 *      (`other_device`) when the browser-side
 *      hybrid-feasibility probe is anything other than an
 *      explicit `false`; degrades to `this_device` when hybrid
 *      is known-infeasible AND a platform authenticator is
 *      available; degrades to `security_key` when neither
 *      transport is reachable. Pass `capability` via
 *      `CeremonyTransportOptions` so the resolver knows; omit
 *      it (or pass `null` for "probe still in flight") and the
 *      resolver returns mobile-first which is also the legacy
 *      hardcoded default. Either way the FU-5 late-arrival
 *      re-seed will replace the initial pick once the probe
 *      lands (gated on `hasPickedRef`).
 *
 * # Usage
 *
 * ```ts
 * // Legacy two-layer callers (no recommendation, no server pref)
 * const transport = useCeremonyTransport();
 *
 * // Three-layer callers (sign-in / enrollment with a
 * // recommender result on hand, no server pref yet)
 * const transport = useCeremonyTransport(
 *   methodToTransport(recommendedDefault?.method),
 * );
 *
 * // Four-layer callers (post-/identify surface with server pref)
 * const transport = useCeremonyTransport(
 *   methodToTransport(recommendedDefault?.method),
 *   { serverPreference: state.preferredTransport },
 * );
 *
 * // ...in a TransportPicker:
 * <TransportPicker
 *   value={transport.value}
 *   onChange={transport.set}
 *   onContinue={(t) => transport.set(t) /\* run ceremony *\/}
 * />
 *
 * // After ceremony success:
 * transport.commit();
 * ```
 */
export interface CeremonyTransportState {
  /** Currently-selected transport. */
  value: PasskeyTransport;
  /** Picker click handler — fires on every card change. Does NOT
   *  persist; persistence is gated on ceremony success via
   *  `commit()` so a failed retry with a different transport
   *  doesn't overwrite a previously-working choice. */
  set: (next: PasskeyTransport) => void;
  /** Persist the current value to long-term storage. Call on
   *  ceremony success; subsequent visits to any ceremony surface
   *  will preselect this card. */
  commit: () => void;
  /** I8 (strategy 2026-05-25) — explicit escape hatch for the
   *  enrollment surfaces that emit `EnrollmentChoice` not
   *  `PasskeyTransport`. When the user picks the `pureid_qr`
   *  flow channel, the success path calls `commitPickAs("other_device")`
   *  so the *user-visible card identity* ("Use another device")
   *  is what's remembered, not the mechanism ("pureid_qr" — which
   *  is not a valid stored transport). Without this, a successful
   *  pureid-QR enrollment would never write to localStorage and
   *  the user's pick would only be remembered by coincidence
   *  (the layer-4 capability default happens to land on
   *  `other_device` for hybrid-capable devices).
   *
   *  Callers that picked a real PasskeyTransport should use
   *  `commit()`; the `commitPickAs` form is explicit at the call
   *  site so a future refactor that drops the if-filter logic
   *  is visible. */
  commitPickAs: (asTransport: PasskeyTransport) => void;
}

/**
 * Optional inputs to the hook beyond the legacy `recommended`
 * positional parameter. New surfaces should prefer this options
 * shape so additional layers (server pref, future signals)
 * extend cleanly without positional-argument churn.
 */
export interface CeremonyTransportOptions {
  /**
   * Re-authentication mode. Do not let a transport remembered from a sign-in or enrollment
   * silently choose how a later approval is presented. A step-up begins on the device where the
   * protected action was clicked; another device remains an explicit picker choice.
   */
  ignoreRememberedPreference?: boolean;
  /**
   * The user's server-side cross-device preference, surfaced
   * by /identify's KnownUserResult.preferred_transport (H-1).
   *
   * Vocabulary on the wire is the canonical authpreferences
   * enum: `this_device | phone_qr | security_key`. The hook
   * translates `phone_qr → other_device` to align with the
   * PasskeyTransport enum used in the picker. Any value
   * outside the recognised set is ignored (the hook falls
   * through to the next layer cleanly), so a future enum
   * addition on the backend cannot break the picker.
   *
   * Undefined / null when:
   *   - the surface hasn't run /identify yet
   *   - /identify ran but no preference row existed
   *   - the lookup failed (server omitted the field)
   *
   * The hook reads this ONCE at first render; later mutations
   * of the param do not re-seed the picker, preserving the
   * "user's intermediate click is respected" invariant.
   */
  serverPreference?: string | null;
  /**
   * Browser capability snapshot (from `probeCapabilities()` in
   * @pureid/auth-core). Feeds the layer-4 fallback so a no-BLE
   * device doesn't seed the picker to phone-QR — the chronic
   * cause of the "user lands on a card that immediately fails"
   * dead-end. `null` (probe still in flight) is permissive:
   * the resolver returns mobile-first, matching the legacy
   * hardcoded default. The hook's late-arrival useEffect re-
   * runs the cascade when this changes, so the picker re-seeds
   * once the probe lands — gated on `hasPickedRef` so a user
   * who has already clicked keeps their pick.
   *
   * Both signals (`hybridFeasibility`, `platformAuthenticator`)
   * are tri-valued (`true | false | null`) — see
   * `defaultTransportForCapability` for the resolution rule.
   */
  capability?: CapabilitySnapshot | null;
  /**
   * ENROLLMENT mode. The enrollment card set is { this_device, pureid_qr,
   * security_key } — the phone path is `pureid_qr`, NOT `other_device`. But every
   * resolution layer here can produce `other_device`: a returning user's stored
   * preference, a `phone_qr` server pref, or the capability fallback (mobile-first
   * on a hybrid-capable device). Seeding `other_device` at enrollment leaves NO
   * card selected while Continue stays enabled — the reported "first option isn't
   * selected" bug. When true, a resolved `other_device` is remapped to
   * `this_device` (the safe default), so the enrollment picker always lands on a
   * visible card. Sign-in leaves this false: there, `other_device` IS a card.
   */
  enrollment?: boolean;
}

/**
 * Translate the backend's canonical authpreferences vocabulary
 * (`this_device | phone_qr | security_key`) into the picker's
 * PasskeyTransport enum (`this_device | other_device |
 * security_key`). Returns null on any unrecognised value so the
 * caller falls through to the next layer in the cascade. Pure;
 * exported for tests + potential reuse.
 */
export function translateServerPreference(
  raw: string | null | undefined,
): PasskeyTransport | null {
  switch (raw) {
    case "this_device":
      return "this_device";
    case "phone_qr":
      return "other_device";
    case "security_key":
      return "security_key";
    default:
      return null;
  }
}

/**
 * Hook accepts an optional `recommended` transport — typically
 * the result of `methodToTransport(recommendedDefault?.method)`
 * — and an optional `options.serverPreference` from the H-1
 * surface. Resolution order is documented at the top of this
 * file: localStorage → serverPreference → recommended →
 * product default.
 *
 * `methodToTransport` returns null for non-transport methods
 * (`tap` / `fragile_redirect` / `none`); pass that null through
 * unchanged — the hook falls through to the product default.
 */
export function useCeremonyTransport(
  recommended?: PasskeyTransport | null,
  options?: CeremonyTransportOptions,
): CeremonyTransportState {
  // FU-5: track whether the user has manually picked a card.
  // A ref (not state) because the gate doesn't drive rendering —
  // it gates the late-arriving auto-adopt effect below.
  const hasPickedRef = useRef<boolean>(false);

  // Initial value via the lazy useState initializer. Same cascade
  // as before; the layer-4 fallback is now capability-aware
  // (mobile-first when the device can do hybrid; degrades to
  // this_device / security_key when not).
  // Resolve the cascade, then (enrollment only) remap `other_device` — which has no enrollment card — to
  // `this_device` so the enrollment picker always lands on a visible card. See CeremonyTransportOptions.enrollment.
  const resolveSeed = useCallback((): PasskeyTransport => {
    const v = computeAnchoredValue(
      options?.serverPreference,
      recommended,
      options?.capability,
      options?.ignoreRememberedPreference,
    );
    return options?.enrollment && v === "other_device" ? "this_device" : v;
  }, [recommended, options?.serverPreference, options?.capability, options?.enrollment, options?.ignoreRememberedPreference]);
  const [value, setValue] = useState<PasskeyTransport>(resolveSeed);

  // FU-5: late-arriving anchor adoption.
  //
  // The pre-FU-5 hook resolved once at mount and never updated.
  // For first-time users on a fresh device (no localStorage, no
  // server pref) whose page mounted BEFORE the recommendation
  // query resolved, that meant the picker locked to
  // PASSKEY_TRANSPORT_DEFAULT even when the
  // recommender later said the device should use
  // platform_passkey. Downstream consequences:
  //   - DD-6 never collapsed (gate failed:
  //     pickerTransport !== recommendedTransport).
  //   - The 3-card picker pre-selected the WRONG card relative
  //     to the recommendation.
  //   - H-1's cross-device server preference, which arrives via
  //     /identify AFTER mount, never seeded the picker either.
  //
  // The fix: re-run the cascade when recommended or
  // serverPreference change, but ONLY if the user hasn't
  // committed to a card yet. Once they click, their pick is
  // sticky for the rest of the mount — the contract the
  // pre-FU-5 comment promised was "the recommendation is a
  // first-visit default, not a live binding," and that contract
  // is now correctly bounded by "first-visit AND user-has-not-
  // touched-the-picker."
  useEffect(() => {
    if (hasPickedRef.current) return;
    const next = resolveSeed();
    // Avoid a no-op setValue (would still be fine — React bails
    // on identical state — but the explicit check keeps the
    // intent clear).
    setValue((prev) => (prev === next ? prev : next));
  }, [resolveSeed]);

  // FU-5: wrap setValue to flip the picker-touched flag. Every
  // caller (TransportChooser, TransportPicker, the DD-6 escape
  // hatch) routes through this `set` exported on the returned
  // state, so capturing the flag here is sufficient.
  const set = useCallback((next: PasskeyTransport) => {
    hasPickedRef.current = true;
    setValue(next);
  }, []);

  const commit = useCallback(() => {
    savePreferredPasskeyTransport(value);
  }, [value]);

  const commitPickAs = useCallback((asTransport: PasskeyTransport) => {
    // I8 — persist the user-visible card identity, regardless
    // of what the hook's current `value` is. Used by the
    // pureid_qr success path so the next sign-in's cascade
    // sees `other_device` (the card the user picked) instead
    // of falling through to the layer-4 default.
    savePreferredPasskeyTransport(asTransport);
  }, []);

  return { value, set, commit, commitPickAs };
}

/**
 * computeAnchoredValue evaluates the 4-layer cascade once
 * (localStorage → server pref → recommendation → capability-
 * aware product default). Returned by both the lazy useState
 * initializer and the late-arrival useEffect so the two paths
 * can't diverge.
 *
 * # Capability-aware viability gate (2026-05-26)
 *
 * Each non-default layer's candidate is filtered through
 * `isTransportViable(candidate, capability)` before being
 * returned. The original cascade was strictly priority-ordered:
 * localStorage > server pref > recommendation > default. That
 * works when every layer's value is achievable on the current
 * device, but it dead-ends in a specific real-world scenario —
 *
 *   A worker's localStorage says "this_device" (last sign-in
 *   was on their Mac with Touch ID). They sit down at a loaner
 *   Windows laptop with no platform authenticator. The cascade
 *   returns "this_device". The step-up dialog's streamlined
 *   "Verify with this device" button fails immediately — the
 *   OS picker has no platform credential to surface.
 *
 * Pre-2026-05-26 the picker masked this: the user mentally
 * skipped the broken card. With the post-2026-05-26 streamlined
 * step-up surface, the broken transport is the ONLY visible
 * affordance, so the failure becomes the user's first
 * interaction. The viability gate downgrades upfront: if
 * capability KNOWS a transport won't work, the layer is skipped
 * and the next viable candidate is returned. Capability
 * indeterminate (`null` / probe in flight) is permissive — we
 * never downgrade a user's saved pick on missing information.
 *
 * Pure helper (no hooks) — safe to call from either context.
 */
function computeAnchoredValue(
  serverPreference: string | null | undefined,
  recommended: PasskeyTransport | null | undefined,
  capability: CapabilitySnapshot | null | undefined,
  ignoreRememberedPreference = false,
): PasskeyTransport {
  if (!ignoreRememberedPreference) {
    const fromLocal = loadPreferredPasskeyTransportOrNull();
    if (fromLocal && isTransportViable(fromLocal, capability)) return fromLocal;
    const fromServer = translateServerPreference(serverPreference);
    if (fromServer && isTransportViable(fromServer, capability)) return fromServer;
  }
  if (recommended && isTransportViable(recommended, capability)) return recommended;
  return defaultTransportForCapability(capability);
}

/**
 * isTransportViable answers "can this device actually perform
 * this transport, given what we know right now?" — used to
 * downgrade a saved/server/recommended pick that the capability
 * probe explicitly rules out.
 *
 * Tri-valued semantics from CapabilitySnapshot:
 *
 *   - `true`   → known viable (transport admitted)
 *   - `false`  → known NOT viable (transport rejected, downgrade)
 *   - `null`   → unknown (permissive — return true, do not
 *                downgrade on missing information)
 *
 * Capability-snapshot mapping:
 *
 *   - `this_device` ⇆ `capability.platformAuthenticator`
 *     A user's Mac with Touch ID has `platformAuthenticator =
 *     true`; a Windows machine with no Hello sensor has
 *     `false`; a browser that doesn't expose the indicator
 *     has `null`.
 *   - `other_device` ⇆ `capability.hybridFeasibility`
 *     A modern desktop with Bluetooth has `true`; a stripped
 *     Linux VM with no BLE stack has `false`; a browser that
 *     refuses to probe has `null`.
 *   - `security_key` is unconditionally viable — there is no
 *     pre-flight signal for "is a USB/NFC key present", and the
 *     OS picker handles the "plug it in now" prompt natively.
 *     Always-admit is the right behavior; the worst case is the
 *     OS picker invites a key tap that never happens, which is
 *     the picker's job not ours.
 *
 * Exhaustive switch on PasskeyTransport so a future transport
 * addition is a compile error here — keeps the gate honest as
 * the enum grows.
 *
 * Exported so consumers can detect the "hook's state is one
 * frame behind the probe" intermediate render (between probe
 * resolution and the late-arrival useEffect committing the
 * downgrade) and suppress UI that depends on a viable
 * transport. The shared StepUpSurface's streamlined view uses
 * this to gate its render: if the current transport is not
 * viable for the resolved capability, the hook will downgrade
 * on the next tick, so render the probing placeholder one more
 * frame instead of flickering the button label.
 */
export function isTransportViable(
  t: PasskeyTransport,
  capability: CapabilitySnapshot | null | undefined,
): boolean {
  if (!capability) return true;
  switch (t) {
    case "this_device":
      return capability.platformAuthenticator !== false;
    case "other_device":
      return capability.hybridFeasibility !== false;
    case "security_key":
      return true;
  }
}
